import * as React from 'react';

/**
 * This is a wrapper that establishes a new heading level for its children. It is intended for use in combination
 * with a heading so that all subsequent headings are nested one level deeper, but can also be used on its own when it's
 * not practical to combine it with a heading.
 *
 * This API is similar to [react-headings](https://www.npmjs.com/package/react-headings)
 *
 * @example
 * Here's an example where "My friends" is inferred as H2, while all subsequent headings are H3s.
 * ```tsx
 * <HeadingLevel component={<Heading variant="heading-lg/bold">My friends</Heading>}>
 *   <Heading variant="eyebrow">Online now</Heading>
 *   ...
 *   <Heading variant="eyebrow">Recently active</Heading>
 *   ...
 *   <Heading variant="eyebrow">Offline</Heading>
 *   ...
 * </HeadingLevel>
 * ```
 *
 * @example
 * It's also possible to force a new heading level, which is useful for for things like popouts (note that the
 * BasePopout component already establishes a new heading level).
 * ```tsx
 * <HeadingLevel forceLevel={2}>
 *   <Heading variant="heading-lg/bold">Report Message</Heading>
 *   ...
 * </HeadingLevel>
 * ```
 */
export function HeadingLevel({component, children, forceLevel}: HeadingLevelProps) {
  const context = React.useContext(HeadingLevelContext);

  const [used, setUsed] = React.useState(component != null);

  let componentLevel = 1;
  let childrenLevel = 1;

  if (forceLevel != null) {
    if (component != null) {
      componentLevel = forceLevel;
      childrenLevel = forceLevel + 1;
    } else {
      childrenLevel = forceLevel;
    }
  } else {
    if (component != null) {
      componentLevel = context.level;
      childrenLevel = context.level + 1;
    } else {
      childrenLevel = context.level + (used ? 1 : 0);
    }
  }

  const componentLevelValue = React.useMemo(
    () => ({
      level: componentLevel,
      getLevelAndMarkUsed: () => {
        setUsed(true);
        return componentLevel;
      },
    }),
    [componentLevel],
  );

  const childrenLevelValue = React.useMemo(
    () => ({
      level: childrenLevel,
      getLevelAndMarkUsed: () => {
        setUsed(true);
        return childrenLevel;
      },
    }),
    [childrenLevel],
  );

  return (
    <>
      {component != null ? (
        <HeadingLevelContext.Provider value={componentLevelValue}>{component}</HeadingLevelContext.Provider>
      ) : null}

      <HeadingLevelContext.Provider value={childrenLevelValue}>{children}</HeadingLevelContext.Provider>
    </>
  );
}

/**
 * If the `Heading` component does not suit your needs (which is unlikely), or if you're creating a visually hidden
 * heading, you can use this component to get a heading tag where the level is automatically inferred from context.
 *
 * @example
 * ```tsx
 * function MyThing() {
 *   return <H>Hello world!</H>;
 * }
 * ```
 */
export function H(props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>) {
  const level = usePrivateHeadingLevel();

  const Tag = `h${Math.min(level, 6)}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  return (
    <Tag
      // Eventually to be used for an accessibility runtime check
      {...(level > 6 ? {'data-excessive-heading-level': level} : {})}
      {...props}
    />
  );
}

interface HeadingLevelProps {
  /**
   * A function that is given the current heading level and returns the heading to render.
   */
  component?: React.ReactNode;

  /**
   * Force the level for this heading. Use with care, and keep in mind that there should only be one H1 on the screen
   * at a time.
   */
  forceLevel?: 1 | 2 | 3 | 4 | 5 | 6;

  children: React.ReactNode;
}

interface HeadingLevelContextValue {
  getLevelAndMarkUsed: () => number;
  level: number;
}

const HeadingLevelContext = React.createContext<HeadingLevelContextValue>({
  getLevelAndMarkUsed: () => 2,
  level: 2,
});
HeadingLevelContext.displayName = 'HeadingLevelContext';

/**
 * Not intended for use outside the `Heading` component.
 *
 * Use the `Heading` component instead (which infers the correct level) or, if needed, use the `H` component.
 */
export function usePrivateHeadingLevel() {
  const {getLevelAndMarkUsed} = React.useContext(HeadingLevelContext);
  return getLevelAndMarkUsed();
}
